import React, { useEffect, useMemo, useState } from "react";
import DOMPurify from "dompurify";
import {
  FaBell,
  FaCheckCircle,
  FaClipboardList,
  FaClock,
  FaEnvelopeOpenText,
  FaFlag,
  FaSearch,
  FaTimes,
} from "react-icons/fa";

import Modal from "../../../../../hooks/Modal";
import { formatDateTime } from "../utils/siteActivityFormatters";

const cn = (...classes) => classes.filter(Boolean).join(" ");

function stripHtml(html = "") {
  if (typeof document === "undefined") {
    return String(html || "").replace(/<[^>]+>/g, " ");
  }

  const element = document.createElement("div");
  element.innerHTML = DOMPurify.sanitize(html);
  return element.textContent || element.innerText || "";
}

function normalizeSearch(value = "") {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function getItemKey(item, index) {
  return item?.id || item?._id || item?.created_at || item?.createdAt || index;
}

function getNotificationContent(item = {}) {
  return item?.message || item?.title || item?.description || "";
}

function getRequestDescription(item = {}) {
  return stripHtml(item?.description || item?.content || item?.message || "");
}

function getPriorityTone(priority = "") {
  const value = normalizeSearch(priority);

  if (value.includes("high") || value.includes("haute") || value.includes("urgent")) {
    return "bg-rose-100 text-rose-700 ring-rose-200 dark:bg-rose-500/10 dark:text-rose-200 dark:ring-rose-500/20";
  }

  if (value.includes("low") || value.includes("basse")) {
    return "bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-200 dark:ring-emerald-500/20";
  }

  return "bg-amber-100 text-amber-700 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-200 dark:ring-amber-500/20";
}

function getTypeConfig(type, siteName) {
  if (type === "messages") {
    return {
      icon: FaEnvelopeOpenText,
      title: `Messages — ${siteName}`,
      subtitle: "Messages reçus depuis le formulaire ou l’espace client.",
      label: "Messages",
      badge:
        "bg-sky-100 text-sky-700 ring-sky-200 dark:bg-sky-500/10 dark:text-sky-200 dark:ring-sky-500/20",
      gradient: "from-sky-500 to-cyan-500",
    };
  }

  if (type === "requests") {
    return {
      icon: FaClipboardList,
      title: `Requêtes en attente — ${siteName}`,
      subtitle: "Demandes clients encore à traiter ou à prioriser.",
      label: "Requêtes",
      badge:
        "bg-amber-100 text-amber-700 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-200 dark:ring-amber-500/20",
      gradient: "from-amber-500 to-orange-500",
    };
  }

  return {
    icon: FaBell,
    title: `Notifications — ${siteName}`,
    subtitle: "Notifications non lues détectées pour la période sélectionnée.",
    label: "Notifications",
    badge:
      "bg-violet-100 text-violet-700 ring-violet-200 dark:bg-violet-500/10 dark:text-violet-200 dark:ring-violet-500/20",
    gradient: "from-violet-500 to-fuchsia-500",
  };
}

function ExpandButton({ expanded, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="mt-3 inline-flex items-center justify-center rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-slate-600 transition hover:border-primary/40 hover:bg-primary/5 hover:text-primary dark:border-slate-700 dark:bg-slate-950 dark:text-slate-300 dark:hover:border-primary/40 dark:hover:bg-primary/10"
    >
      {expanded ? "Réduire" : "Voir plus"}
    </button>
  );
}

function MetaDate({ value }) {
  return (
    <div className="mt-4 flex items-center gap-2 text-xs font-semibold text-slate-400 dark:text-slate-500">
      <FaClock className="text-slate-300 dark:text-slate-600" />
      <span>{formatDateTime(value)}</span>
    </div>
  );
}

function MessageCard({ item, index, expanded, onToggle }) {
  const clean = DOMPurify.sanitize(item?.content || "");
  const plainText = stripHtml(item?.content || "");
  const isLong = plainText.length > 420;
  const sender =
    item?.name ||
    item?.sender_name ||
    item?.nom ||
    item?.prenom ||
    item?.email ||
    item?.mail ||
    "";

  return (
    <article className="group relative overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:border-sky-200 hover:shadow-lg dark:border-slate-800 dark:bg-slate-900 dark:hover:border-sky-500/30">
      <div className="absolute left-0 top-0 h-full w-1 bg-gradient-to-b from-sky-500 to-cyan-400" />

      <div className="p-4 sm:p-5">
        <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <div className="inline-flex items-center gap-2 rounded-full bg-sky-50 px-3 py-1 text-xs font-bold text-sky-700 ring-1 ring-sky-100 dark:bg-sky-500/10 dark:text-sky-200 dark:ring-sky-500/20">
              <FaEnvelopeOpenText />
              Message #{index + 1}
            </div>

            {sender && (
              <div className="mt-2 text-sm font-bold text-slate-700 dark:text-slate-200">
                {sender}
              </div>
            )}
          </div>
        </div>

        <div
          className={cn(
            "relative rounded-2xl border border-slate-100 bg-slate-50/80 p-4 dark:border-slate-800 dark:bg-slate-950/50",
            isLong && !expanded && "max-h-56 overflow-hidden",
          )}
        >
          <div
            className="prose prose-sm max-w-none text-slate-700 prose-a:font-bold prose-a:text-primary dark:prose-invert dark:text-slate-200"
            dangerouslySetInnerHTML={{ __html: clean }}
          />

          {isLong && !expanded && (
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-slate-50 to-transparent dark:from-slate-950" />
          )}
        </div>

        {isLong && <ExpandButton expanded={expanded} onClick={onToggle} />}

        <MetaDate value={item?.created_at || item?.createdAt} />
      </div>
    </article>
  );
}

function RequestCard({ item, expanded, onToggle }) {
  const description = getRequestDescription(item);
  const isLong = description.length > 320;
  const priority = item?.priority || "Medium";

  return (
    <article className="group relative overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:border-amber-200 hover:shadow-lg dark:border-slate-800 dark:bg-slate-900 dark:hover:border-amber-500/30">
      <div className="absolute left-0 top-0 h-full w-1 bg-gradient-to-b from-amber-500 to-orange-400" />

      <div className="p-4 sm:p-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-amber-50 px-3 py-1 text-xs font-bold text-amber-700 ring-1 ring-amber-100 dark:bg-amber-500/10 dark:text-amber-200 dark:ring-amber-500/20">
              <FaClipboardList />
              Requête client
            </div>

            <h4 className="text-base font-bold text-[#123B57] dark:text-white">
              {item?.title || "Requête sans titre"}
            </h4>
          </div>

          <div className="flex shrink-0 flex-wrap gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-rose-100 px-3 py-1 text-xs font-bold text-rose-700 ring-1 ring-rose-200 dark:bg-rose-500/10 dark:text-rose-200 dark:ring-rose-500/20">
              <FaClock />
              En attente
            </span>

            <span
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ring-1",
                getPriorityTone(priority),
              )}
            >
              <FaFlag />
              {priority}
            </span>
          </div>
        </div>

        {description ? (
          <div
            className={cn(
              "mt-4 rounded-2xl border border-slate-100 bg-slate-50/80 p-4 text-sm leading-relaxed text-slate-600 dark:border-slate-800 dark:bg-slate-950/50 dark:text-slate-300",
              isLong && !expanded && "max-h-28 overflow-hidden",
            )}
          >
            {description}
          </div>
        ) : (
          <div className="mt-4 rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-4 text-sm text-slate-400 dark:border-slate-800 dark:bg-slate-950/40">
            Aucune description renseignée.
          </div>
        )}

        {isLong && <ExpandButton expanded={expanded} onClick={onToggle} />}

        <MetaDate value={item?.created_at || item?.createdAt} />
      </div>
    </article>
  );
}

function NotificationCard({ item, index, expanded, onToggle }) {
  const content = getNotificationContent(item);
  const isLong = String(content || "").length > 320;

  return (
    <article className="group relative overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:border-violet-200 hover:shadow-lg dark:border-slate-800 dark:bg-slate-900 dark:hover:border-violet-500/30">
      <div className="absolute left-0 top-0 h-full w-1 bg-gradient-to-b from-violet-500 to-fuchsia-400" />

      <div className="p-4 sm:p-5">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div className="inline-flex items-center gap-2 rounded-full bg-violet-50 px-3 py-1 text-xs font-bold text-violet-700 ring-1 ring-violet-100 dark:bg-violet-500/10 dark:text-violet-200 dark:ring-violet-500/20">
            <FaBell />
            Notification #{index + 1}
          </div>

          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-700 ring-1 ring-emerald-100 dark:bg-emerald-500/10 dark:text-emerald-200 dark:ring-emerald-500/20">
            <FaCheckCircle />
            Non lue
          </span>
        </div>

        <p
          className={cn(
            "rounded-2xl border border-slate-100 bg-slate-50/80 p-4 text-sm leading-relaxed text-slate-700 dark:border-slate-800 dark:bg-slate-950/50 dark:text-slate-200",
            isLong && !expanded && "max-h-28 overflow-hidden",
          )}
        >
          {content || "Notification sans message"}
        </p>

        {isLong && <ExpandButton expanded={expanded} onClick={onToggle} />}

        <MetaDate value={item?.created_at || item?.createdAt} />
      </div>
    </article>
  );
}

export default function ActivityDetailsModal({ details, onClose }) {
  const [query, setQuery] = useState("");
  const [expandedItems, setExpandedItems] = useState({});

  const items = details?.items || [];
  const type = details?.type || "notifications";
  const siteName = details?.siteName || "Site";

  const config = useMemo(() => getTypeConfig(type, siteName), [type, siteName]);
  const Icon = config.icon;

  useEffect(() => {
    setQuery("");
    setExpandedItems({});
  }, [details]);

  const filteredItems = useMemo(() => {
    const search = normalizeSearch(query);

    return items.filter((item) => {
      const content =
        type === "messages"
          ? stripHtml(item?.content || "")
          : type === "requests"
            ? `${item?.title || ""} ${getRequestDescription(item)} ${item?.priority || ""}`
            : getNotificationContent(item);

      return !search || normalizeSearch(content).includes(search);
    });
  }, [items, query, type]);

  if (!details) return null;

  const toggleExpanded = (key) => {
    setExpandedItems((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  return (
    <Modal
      onClose={onClose}
      title={config.title}
      subtitle={config.subtitle}
      size="xl"
      contentClassName="p-0"
    >
      <div className="flex max-h-[78vh] flex-col">
        <div className="sticky top-0 z-10 border-b border-slate-200 bg-white/95 p-4 backdrop-blur dark:border-slate-800 dark:bg-slate-900/95 sm:p-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-3">
              <div
                className={cn(
                  "flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br text-white shadow-lg",
                  config.gradient,
                )}
              >
                <Icon />
              </div>

              <div>
                <div
                  className={cn(
                    "inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-bold ring-1",
                    config.badge,
                  )}
                >
                  {config.label}
                </div>

                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  {filteredItems.length} affiché
                  {filteredItems.length > 1 ? "s" : ""} sur {items.length} élément
                  {items.length > 1 ? "s" : ""}
                </p>
              </div>
            </div>

            <div className="relative w-full lg:max-w-md">
              <FaSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />

              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Rechercher dans le contenu..."
                className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 pl-11 pr-11 text-sm font-semibold text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-primary/50 focus:bg-white focus:ring-4 focus:ring-primary/10 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:bg-slate-900"
              />

              {query && (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  className="absolute right-3 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full bg-slate-200 text-slate-500 transition hover:bg-rose-100 hover:text-rose-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-rose-950/50 dark:hover:text-rose-300"
                  aria-label="Effacer la recherche"
                >
                  <FaTimes className="text-xs" />
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto bg-slate-50/80 p-4 dark:bg-slate-950/40 sm:p-5">
          {filteredItems.length === 0 ? (
            <div className="flex min-h-[260px] flex-col items-center justify-center rounded-[28px] border border-dashed border-slate-300 bg-white p-8 text-center dark:border-slate-700 dark:bg-slate-900">
              <div
                className={cn(
                  "mb-4 flex h-16 w-16 items-center justify-center rounded-3xl bg-gradient-to-br text-white shadow-lg",
                  config.gradient,
                )}
              >
                <Icon className="text-xl" />
              </div>

              <h3 className="text-lg font-bold text-[#123B57] dark:text-white">
                Aucun élément à afficher
              </h3>

              <p className="mt-2 max-w-md text-sm text-slate-500 dark:text-slate-400">
                Aucun résultat ne correspond à ta recherche ou à la période sélectionnée.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredItems.map((item, index) => {
                const key = getItemKey(item, index);
                const expanded = !!expandedItems[key];

                if (type === "messages") {
                  return (
                    <MessageCard
                      key={key}
                      item={item}
                      index={index}
                      expanded={expanded}
                      onToggle={() => toggleExpanded(key)}
                    />
                  );
                }

                if (type === "requests") {
                  return (
                    <RequestCard
                      key={key}
                      item={item}
                      expanded={expanded}
                      onToggle={() => toggleExpanded(key)}
                    />
                  );
                }

                return (
                  <NotificationCard
                    key={key}
                    item={item}
                    index={index}
                    expanded={expanded}
                    onToggle={() => toggleExpanded(key)}
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}